import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import consts from "../../constant";
import axios from "axios";

interface PairState {
    pairs: any | null;
    isLoading: boolean;
    error: string | null;
    tradeHistory: any | null;
    ordersOpen: any | null;
    favPairs: any | null;
}

const url = `${consts.BackendUrl}`;

// ✅ Correct names for Thunks
export const getPairList = createAsyncThunk("pair/getPairList", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(`${url}/binance/getpairs`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("crymadx")}`,
            },
        });
        if (!response?.data?.success) {
            return rejectWithValue(response?.data?.message || "Failed To Get Pair List");
        }
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data?.message || "Failed To Get Pair List");
        }
        return rejectWithValue("An unknown error occurred during Get Pair List!");
    }
});

export const getHistory = createAsyncThunk("pair/getTradeHistory", async (payload: any, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${url}/binance/history`, payload, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("crymadx")}`,
            },
        });
        if (!response?.data?.success) {
            return rejectWithValue(response?.data?.message || "Failed To Get Trade History");
        }
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data?.message || "Failed To Get Trade History");
        }
        return rejectWithValue("An unknown error occurred during Get Trade History!");
    }
});

export const getOpenOrder = createAsyncThunk("pair/getOpenOrders", async (payload: any, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${url}/binance/openorders`, payload, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("crymadx")}`,
            },
        });
        if (!response?.data?.success) {
            return rejectWithValue(response?.data?.message || "Failed To Get Open Orders");
        }
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data?.message || "Failed To Get Open Orders");
        }
        return rejectWithValue("An unknown error occurred during Fetching Open Orders!");
    }
});


export const placeOrder = createAsyncThunk("pair/placeOrder", async (payload: any, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${url}/binance/order`, payload, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("crymadx")}`,
            },
        });
        if (!response?.data?.success) {
            return rejectWithValue(response?.data?.message || "Failed To Place Order");
        }
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data?.message || "Failed To Place Order");
        }
        return rejectWithValue("An unknown error occurred during Placing Order!");
    }
});

// ❌ you gave wrong name here ("auth/placeOrder")
// ✅ Correct it!
export const cancelOrder = createAsyncThunk("pair/cancelOrder", async (payload: any, { rejectWithValue }) => {
    try {
        const response = await axios.post(`${url}/binance/cancelorder`, payload, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("crymadx")}`,
            },
        });
        if (!response?.data?.success) {
            return rejectWithValue(response?.data?.message || "Failed To Cancel Order");
        }
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data?.message || "Failed To Cancel Order");
        }
        return rejectWithValue("An unknown error occurred during Cancelling Order!");
    }
});

export const getFavPair = createAsyncThunk("pair/getFavPairs", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(`${url}/getfav`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("crymadx")}`,
            },
        });
        if (!response?.data?.success) {
            return rejectWithValue(response?.data?.message || "Failed To Get Fav Pair List");
        }
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            return rejectWithValue(error.response.data?.message || "Failed To Get Fav Pair List");
        }
        return rejectWithValue("An unknown error occurred during Get Fav Pair List!");
    }
});

// --- Initial state
const initialState: PairState = {
    pairs: null,
    isLoading: false,
    error: null,
    tradeHistory: null,
    ordersOpen: null,
    favPairs: null,
};

// ✅ Rename slice to `pair`
const pairSlice = createSlice({
    name: "pair",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(getPairList.pending, (state) => { state.isLoading = true; })
            .addCase(getPairList.fulfilled, (state, action) => {
                state.isLoading = false;
                state.pairs = action.payload || null;
            })
            .addCase(getPairList.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string | null;
            })

            // Fav Pairs
            .addCase(getFavPair.pending, (state) => { state.isLoading = true; })
            .addCase(getFavPair.fulfilled, (state, action) => {
                state.isLoading = false;
                state.favPairs = action.payload || null;
            })
            .addCase(getFavPair.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string | null;
            })

            // Trade History
            .addCase(getHistory.pending, (state) => { state.isLoading = true; })
            .addCase(getHistory.fulfilled, (state, action) => {
                state.isLoading = false;
                state.tradeHistory = action.payload || null;
            })
            .addCase(getHistory.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string | null;
            })

            // Open Orders
            .addCase(getOpenOrder.pending, (state) => { state.isLoading = true; })
            .addCase(getOpenOrder.fulfilled, (state, action) => {
                state.isLoading = false;
                state.ordersOpen = action.payload || null;
            })
            .addCase(getOpenOrder.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string | null;
            });
    },
});

export default pairSlice.reducer;
